import { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    FunctionField,
    SimpleForm,
    TextInput,
    TopToolbar,
    usePermissions,
    useTranslate,
    required,
    AutocompleteInput,
    SelectInput,
    useGetList,
    Edit,
    maxLength,
    minLength,
    useRecordContext,
} from 'react-admin';
import CustomCreate from '@/components/global/list/CustomCreate.jsx';
import CustomList from '@/components/global/list/CustomList.jsx';
import { SearchFilterInput } from '@/components/global/filters/SearchFilterInput.jsx';
import CustomFilterButton from '@/components/global/filters/CustomFilterButton.jsx';
import { CustomSelectColumnsButton } from '@/components/global/buttons/CustomSelectColumnsButton.jsx';
import ColumnsIcon from '@/assets/icons/columns-icon.svg?react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import CustomDatagridHeader from '@/components/global/list/CustomDatagridHeader.jsx';
import { useDrawer } from '@/composables/useDrawer.js';
import { StatusField } from '@/components/billOfMaterials/fields/StatusField.jsx';
import CustomSelectArrayInput from '@/components/global/inputs/CustomSelectArrayInput.jsx';
import { billOfMaterialsStatusChoices } from '@/components/global/options/selectOptions.jsx';
import { SearchBar } from '@/components/global/list/searchBar.jsx';
import { ReferenceArrayFilter } from '@/components/global/filters/filters.jsx';
import { RightDrawer } from '@/components/global/drawers/RightDrawer.jsx';
import CustomCreateButton from '@/components/global/buttons/CustomCreateButton.jsx';
import CustomToolbarActionsPopover from '@/components/global/buttons/CustomToolbarActionsPopover.jsx';
import { EditToolbar } from '@/components/global/buttons/toolbar.jsx';
import TableHeaderCellWithSubtitle from '@/components/global/fields/TableHeaderCellWithSubtitle.jsx';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover.jsx';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import CustomShowModalButton from '@/components/global/buttons/CustomShowModalButton.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import { canViewDetails } from '@/helpers/permissions/canViewDetails.js';

const ListActions = () => {
    const translate = useTranslate();
    const { permissions } = usePermissions();

    return (
        <TopToolbar>
            <SearchFilterInput />
            <CustomFilterButton />
            <CustomSelectColumnsButton startIcon={<ColumnsIcon />} style={{ margin: '0px -4px' }} />
            {permissions?.isAdmin && (
                <CustomToolbarActionsPopover>
                    <CustomCreateButton label={translate('resources.billOfMaterials.labels.create')} />
                </CustomToolbarActionsPopover>
            )}
        </TopToolbar>
    );
};

const MoreActionsPopover = ({ permissions }) => {
    const record = useRecordContext();
    const { openDrawer } = useDrawer();
    const translate = useTranslate();
    const canShow = canViewDetails('spare-part', permissions);

    if (!record || (!canShow && !permissions?.isAdmin)) return null;

    return (
        <CustomMorePopover permissions={permissions} record={record}>
            {canShow && (
                <CustomShowModalButton label={translate('resources.billOfMaterials.labels.show')} onOpen={() => openDrawer(record.id, 'spare-part-show')} />
            )}
            {permissions?.isAdmin && (
                <CustomEditModalButton label={translate('resources.billOfMaterials.labels.edit')} onOpen={() => openDrawer(record.id, 'spare-part-edit')} />
            )}
        </CustomMorePopover>
    );
};

MoreActionsPopover.propTypes = {
    permissions: PropTypes.shape({
        isAdmin: PropTypes.bool,
        isBok: PropTypes.bool,
    }),
};

export const BillOfMaterialsList = () => {
    const { isLoading, permissions } = usePermissions();
    const translate = useTranslate();
    const theme = useTheme();
    const { openDrawer } = useDrawer();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const filters = useMemo(
        () => [
            <SearchBar key='search' source='value' alwaysOn />,
            <CustomSelectArrayInput
                key='filter_status'
                source='status_in'
                label={translate('common.status')}
                choices={billOfMaterialsStatusChoices(translate)}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='filter_module'
                source='module_in'
                label={translate('resources.billOfMaterials.labels.module')}
                reference='spare-part-module'
                alwaysOn={isMobile}
            />,
        ],
        [isMobile, translate],
    );
    const handleRowClick = id => {
        openDrawer(id, 'spare-part-show');
        return false;
    };

    if (isLoading) return null;
    if (!permissions?.isAdmin && !permissions?.isBok) return null;

    return (
        <CustomList filters={filters} filterDefaultValues={{ status_in: ['inUse'] }} actions={<ListActions />} sort={{ field: 'createdAt', order: 'DESC' }}>
            <CustomDatagridConfigurable header={<CustomDatagridHeader />} rowClick={handleRowClick} bulkActionButtons={false}>
                <FunctionField label={translate('resources.billOfMaterials.labels.name')} sortBy='name' render={record => record.name} />
                <FunctionField label={translate('resources.billOfMaterials.labels.module')} sortBy='module' render={record => record.module} />
                <FunctionField
                    label={translate('resources.billOfMaterials.labels.status')}
                    sortBy='status'
                    render={record => <StatusField record={record} />}
                />
                <FunctionField label={translate('resources.billOfMaterials.labels.useCount')} sortBy='useCount' render={record => record.useCount} />
                <FunctionField
                    label={<TableHeaderCellWithSubtitle title={translate('common.options')} />}
                    render={() => (
                        <Box onClick={e => e.stopPropagation()}>
                            <MoreActionsPopover permissions={permissions} />
                        </Box>
                    )}
                />
            </CustomDatagridConfigurable>
        </CustomList>
    );
};

const Form = ({ action = 'create' }) => {
    const translate = useTranslate();

    const { data: modules, isLoading: modulesLoading } = useGetList('spare-part-module', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
    });

    const handleCreateModule = useCallback(
        moduleName => {
            if (!moduleName || typeof moduleName !== 'string') {
                return null;
            }
            moduleName = moduleName.trim();
            const newModule = { id: moduleName, name: moduleName };
            modules.push(newModule);

            return newModule;
        },
        [modules],
    );

    return (
        <SimpleForm toolbar={<EditToolbar />}>
            <TextInput
                name='name'
                source='name'
                label={translate('resources.billOfMaterials.labels.name')}
                fullWidth
                validate={[
                    required(translate('common.validation.required')),
                    minLength(3, translate('common.validation.minLength', { count: 3 })),
                    maxLength(100, translate('common.validation.maxLength', { count: 100 })),
                ]}
            />
            <Box className='autocomplete-parent'>
                <AutocompleteInput
                    name='module'
                    source='module'
                    label={translate('resources.billOfMaterials.labels.module')}
                    choices={modules}
                    validate={[
                        minLength(3, translate('common.validation.minLength', { count: 3 })),
                        maxLength(100, translate('common.validation.maxLength', { count: 100 })),
                    ]}
                    format={value => (value ? value.trim() : value)}
                    fullWidth
                    isLoading={modulesLoading}
                    onCreate={handleCreateModule}
                    noOptionsText={translate('resources.billOfMaterials.labels.noModuleFound')}
                    disablePortal={true}
                />
            </Box>
            {action === 'edit' && (
                <SelectInput
                    name='status'
                    source='status'
                    label={translate('resources.billOfMaterials.labels.status')}
                    choices={billOfMaterialsStatusChoices(translate)}
                    fullWidth
                    validate={required(translate('common.validation.required'))}
                    defaultValue='inUse'
                />
            )}
        </SimpleForm>
    );
};

export const BillOfMaterialsCreate = ({ handleClose = () => {} }) => {
    const { isLoading } = usePermissions();
    const translate = useTranslate();

    if (isLoading) return null;

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.billOfMaterials.labels.create')}>
            <CustomCreate handleClose={handleClose}>
                <Form />
            </CustomCreate>
        </RightDrawer>
    );
};

export const BillOfMaterialsEdit = ({ handleClose = () => {}, elementId }) => {
    const { isLoading } = usePermissions();
    const translate = useTranslate();

    if (isLoading) return null;

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.billOfMaterials.labels.edit')}>
            <Edit id={elementId} mutationMode='pessimistic'>
                <Form action='edit' />
            </Edit>
        </RightDrawer>
    );
};

Form.propTypes = {
    action: PropTypes.oneOf(['create', 'edit']),
};

BillOfMaterialsCreate.propTypes = {
    handleClose: PropTypes.func,
};

BillOfMaterialsEdit.propTypes = {
    handleClose: PropTypes.func,
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
